import locale from "@bonliva-crm/locale";
import {
  Box,
  Divider,
  IconButton,
  makeStyles,
  Modal as MuiModal,
  Theme,
  Typography
} from "@material-ui/core";
import { useLegacyNavigation } from "@navigation";

import React, { ComponentProps, useState } from "react";
import CloseIcon from "../../icons/components/close-icon";
import Button from "../../input/components/button";
import { AlertDialog } from "../../feedback";

type ModalDimensions = {
  minWidth?: string;
  maxWidth?: string;
  width?: string;
  minHeight?: string;
  maxHeight?: string;
  height?: string;
};

type Props = {
  fullHeight?: boolean;
  modalContent?: React.ReactNode;
  leftCta?: React.ReactNode;
  rightCta?: React.ReactNode;
  label?: string;
  labelSubtitle?: string | React.ReactNode;
  saveButtonDisabled?: boolean;
  onSubmit?: () => void;
  onClose?: () => void;
  open: boolean;
  setOpen: (open: boolean) => void;
  dimensions?: ModalDimensions;
  isDirty?: boolean;
  blockId?: string;
  contentContainerStyle?: React.CSSProperties;
  children?: React.ReactNode;
} & Omit<ComponentProps<typeof MuiModal>, "children">;

/**
 * @deprecated use Dialog
 */
export const Modal = React.forwardRef<HTMLDivElement, Props>((props, ref) => {
  const {
    modalContent,
    leftCta,
    rightCta,
    label,
    labelSubtitle,
    saveButtonDisabled,
    onSubmit,
    onClose,
    open,
    setOpen,
    ref: unusedRef,
    dimensions,
    isDirty,
    blockId,
    contentContainerStyle,
    ...modalProps
  } = props;

  const nav = useLegacyNavigation();
  blockId && nav && nav.lockNavigation(blockId, !!isDirty);

  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const classes = useStyles({
    dimensions
  });

  const close = () => {
    blockId && nav && nav.unBlock(blockId);
    setOpen(false);
    setAlertOpen(false);
    onClose && onClose();
  };

  const attemptClose = () => {
    if (isDirty) {
      setAlertOpen(true);
    } else {
      close();
    }
  };

  const submit = () => {
    if (!onSubmit) {
      return;
    }

    setSubmitDisabled(true);
    Promise.resolve(onSubmit()).finally(() => {
      setSubmitDisabled(false);
      close();
    });
  };

  const RightCta = () => {
    if (rightCta) {
      return <Box className={classes.buttonContainer}>{rightCta}</Box>;
    }
    return (
      <Box className={classes.buttonContainer}>
        {onSubmit ? (
          <>
            <Button variant="outlined" color="primary" onClick={() => attemptClose()}>
              {locale.Cancel}
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={saveButtonDisabled || submitDisabled}
              onClick={() => submit()}
            >
              {locale.Save}
            </Button>
          </>
        ) : (
          <Button variant="outlined" color="primary" onClick={() => attemptClose()}>
            {locale.Close1}
          </Button>
        )}
      </Box>
    );
  };

  return (
    <>
      <MuiModal
        open={open}
        onClose={() => attemptClose()}
        className={classes.root}
        {...modalProps}
      >
        <div className={classes.modalContent} ref={ref} style={contentContainerStyle}>
          <Box className={classes.labelContainer}>
            {label && (
              <Typography variant="h3" color="textPrimary" style={{fontSize: 28}}>
                {label}
              </Typography>
            )}
            {labelSubtitle &&
              (typeof labelSubtitle === "string" ? (
                <Typography variant="body1" color="textPrimary">
                  {labelSubtitle}
                </Typography>
              ) : (
                labelSubtitle
              ))}
          </Box>
          <IconButton className={classes.closeButton}>
            <CloseIcon onClick={() => attemptClose()} />
          </IconButton>
          <Box style={{ flexGrow: 1 }}>{modalContent || props.children}</Box>
          <Divider style={{ marginTop: "auto" }} />
          <Box className={classes.ctaContainer}>
            {leftCta}
            <RightCta />
          </Box>
        </div>
      </MuiModal>

      <AlertDialog
        open={alertOpen}
        onOk={close}
        onClose={() => setAlertOpen(false)}
        title={`${locale.continue}?`}
        children={locale.priceLeaveNow}
        okText={locale.Leave}
        cancelText={locale.Stay}
      />
    </>
  );
});

const useStyles = makeStyles<Theme, { dimensions?: ModalDimensions }>((theme) => ({
  root: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  },
  modalContent: (props) => ({
    position: "relative",
    display: "flex",
    flexDirection: "column",
    backgroundColor: theme.palette.background.default,
    padding: "2rem",
    borderRadius: "0.5rem",
    rowGap: "2rem",
    overflowY: "auto",
    "@media only screen and (max-width: 600px)": {
      minWidth: "90%",
      maxWidth: "90%",
      minHeight: "0",
      maxHeight: "90%",
      ...props.dimensions
    },
    "@media only screen and (max-width: 992px)": {
      minWidth: "25%",
      maxWidth: "300px",
      minHeight: "0",
      maxHeight: "90%",
      ...props.dimensions
    },
    "@media only screen and (min-width: 992px)": {
      minWidth: "25%",
      maxWidth: "400px",
      minHeight: "0",
      maxHeight: "90%",
      ...props.dimensions
    },

    ...props.dimensions
  }),
  labelContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
    paddingRight: "3rem"
  },
  ctaContainer: {
    display: "flex",
    justifyContent: "space-between"
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "1rem",
    marginLeft: "auto"
  },
  closeButton: {
    position: "absolute",
    top: "1rem",
    right: "1rem"
  }
}));
